import type React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as tradeInsActionCreators from '@makemydeal/dr-dash-store/actions/tradeInsActionCreators';
import * as tradeActionTypes from '@makemydeal/dr-dash-store/actionTypes/tradeActionTypes';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';

import { GENERIC_LAST_FIELD_CHANGED_ERROR, usePaymentCall, dealLockHooks } from '@makemydeal/dr-dash-components';
import { formatNumberAndCents, kebabCase } from '@makemydeal/dr-common-utils';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

export const useTradeAllowanceField = () => {
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const hasSingleIncludedTradeIn = useSelector(tradeInsSelectors.hasSingleIncludedTradeIn);
    const firstIncludedTradeInIndex = useSelector(tradeInsSelectors.getFirstIncludedTradeInIndex);
    const tradeIns = useSelector(tradeInsSelectors.getActiveTradeIns);
    const isGCDynamicValidationsEnabled = useSelector(featureToggleSelectors.isGCDynamicValidationsEnabled);

    const {
        value: allowance,
        handleChange: changeAllowance,
        handleBlur: allowanceBlur,
        handleKeyDown
    } = usePaymentCall(
        tradeInsSelectors.getIncludedTradeInsTotalValue,
        tradeInsActionCreators.allowanceChanged(firstIncludedTradeInIndex),
        {
            formatValueToString: formatNumberAndCents,
            ignoreTextValue: () => false
        }
    );

    const failedAllowance = useSelector(deskingSelectors.wasTheLastFailure(tradeActionTypes.ALLOWANCE_CHANGED));
    const dispatch = useDispatch();
    const label = tradeIns.length > 1 ? 'Total Trade Allowance' : 'Trade Allowance';
    const fieldName = kebabCase(label);
    const isLocked = dealLockHooks.useIsDealLocked();
    const handleLabelActivate = () => {
        dispatch(baseDealActionCreators.openSlideOut(baseDealActionCreators.SlideOutType.TradeIn));
        dispatch(interactionAnalyticsActionCreators.mvTradeAllowanceLink());
    };

    const shouldSyncRedDotErrors = isDealScreenRedDotErrorsEnabled || isGCDynamicValidationsEnabled;

    useEffect(() => {
        if (shouldSyncRedDotErrors) {
            dispatch(dealScreenErrorActionCreators.setFieldError(DealTabs.SUMMARY, fieldName, failedAllowance));
        }
    }, [shouldSyncRedDotErrors, dispatch, failedAllowance]);

    const handleLabelKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleLabelActivate();
        }
    };

    return {
        label,
        errorBadgeEntity: DealTabs.TRADE_IN,
        onLabelActivate: handleLabelActivate,
        onLabelKeyDown: handleLabelKeyDown,
        inputProps: {
            inputPrefix: '$',
            autoInsertCommas: true,
            allowDecimal: true,
            decimalMaxLength: 2,
            decimalMinLength: 0,
            'data-testid': 'allowance',
            name: fieldName,
            onChange: changeAllowance,
            placeholder: '10,000.00',
            value: allowance,
            hasError: failedAllowance,
            errorMessage: GENERIC_LAST_FIELD_CHANGED_ERROR,
            onBlur: allowanceBlur,
            disabled: isLocked || !hasSingleIncludedTradeIn,
            size: 'sm' as const,
            onKeyDown: handleKeyDown
        }
    };
};
